import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getTrapCapacityAnalytics } from 'api/traps/CapacityAnalytics/getTrapCapacityAnalytics';
import { CapacityAnalyticsParams, CapacityAnalyticsResponse } from 'api/traps/CapacityAnalytics/types';

export const useCapacityAnalytics = (trapSlug: string) => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const [data, setData] = useState<CapacityAnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (params: CapacityAnalyticsParams) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setIsLoading(true);
      setError(null);

      try {
        const response = await getTrapCapacityAnalytics(trapSlug, params, controller.signal);
        setData(response);
      } catch (requestError) {
        if (axios.isCancel(requestError)) {
          return;
        }

        const detail = axios.isAxiosError(requestError)
          ? requestError.response?.data?.detail || requestError.message
          : 'Failed to load capacity analytics';
        setError(typeof detail === 'string' ? detail : 'Failed to load capacity analytics');
        setData(null);
      } finally {
        if (abortControllerRef.current === controller) {
          setIsLoading(false);
        }
      }
    },
    [trapSlug],
  );

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return { data, isLoading, error, load };
};
